import axios from 'axios';
import {
    LoginRequest,
    LoginResponse,
    RegisterRequest,
    RegisterResponse,
    ChangePasswordRequest,
    CashBalance,
    CashPackage,
    PaymentPreference,
    CryptoPaymentDto,
    CryptoPaymentResponse,
    CryptoPaymentStatus,
    NewsItem,
    RankingRow,
    GuildRankingRow,
    LevelRankingRow,
    DownloadLinkItem,
    SiteConfigData,
    SiteCountdownData,
    SiteCountdownUpdateRequest,
    CashShopItemDto,
    CashShopOutboxItemDto,
    CashShopCategoryDto,
    EventPackageInfoDto,
    EventPackageStatusDto,
    EventPackageAdminDto,
    EventPackageAdminCreateRequest,
    EventPackageRedemptionDto,
    StreamerPublicDto,
    StreamerAdminDto,
    StreamerAccountSearchDto,
    StreamerUpdateRequest,
    GuideTreeCategory,
    GuideCategory,
    GuideArticle,
    MountGuideHome,
    MountGuideRegionDetail,
    MountGuideMountDetail,
} from '../types';

// Em produção usamos uma rota relativa (/api) que será
// redirecionada por um proxy reverso (ex.: IIS/Nginx) para a API local.
// Em desenvolvimento você pode sobrescrever com REACT_APP_API_URL.
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Interceptor para adicionar token JWT
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        console.log('Token no localStorage:', token ? 'Presente' : 'Ausente');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            console.log('Authorization header adicionado');
        } else {
            console.log('Nenhum token encontrado, fazendo requisição sem auth');
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

const normalizeStoredUser = (raw: Record<string, unknown> | null | undefined) => {
    if (!raw) return null;
    const user = {
        account: String(raw.account ?? raw.Account ?? ''),
        email: String(raw.email ?? raw.Email ?? ''),
        realBalance: Number(raw.realBalance ?? raw.RealBalance ?? 0),
        bonusBalance: Number(raw.bonusBalance ?? raw.BonusBalance ?? 0),
        isBlocked: Boolean(raw.isBlocked ?? raw.IsBlocked ?? false),
        permission: Number(raw.permission ?? raw.Permission ?? 0),
        createDate: String(raw.createDate ?? raw.CreateDate ?? ''),
        lastAccessDate: raw.lastAccessDate ?? raw.LastAccessDate,
        otpNumber: raw.otpNumber ?? raw.OtpNumber,
        accountDbId: Number(raw.accountDbId ?? raw.AccountDbId ?? 0),
    };
    return user;
};

const ADMIN_CACHE_KEY = 'isAdminVerified';

let adminCheckInFlight: Promise<boolean> | null = null;

const readCachedAdmin = (): boolean | null => {
    const v = sessionStorage.getItem(ADMIN_CACHE_KEY);
    if (v === '1') return true;
    if (v === '0') return false;
    return null;
};

const writeCachedAdmin = (isAdmin: boolean) => {
    sessionStorage.setItem(ADMIN_CACHE_KEY, isAdmin ? '1' : '0');
};

const clearCachedAdmin = () => {
    sessionStorage.removeItem(ADMIN_CACHE_KEY);
    adminCheckInFlight = null;
};

export const authService = {
    login: async (data: LoginRequest): Promise<LoginResponse> => {
        const response = await api.post<LoginResponse>('/auth/login', data);
        if (response.data.success && response.data.token) {
            localStorage.setItem('token', response.data.token);
            const user = normalizeStoredUser(response.data.user as unknown as Record<string, unknown>);
            if (user) {
                localStorage.setItem('user', JSON.stringify(user));
            }
            if (typeof response.data.isAdmin === 'boolean') {
                writeCachedAdmin(response.data.isAdmin);
            } else if (user?.permission === 100) {
                writeCachedAdmin(true);
            }
        }
        return response.data;
    },

    register: async (data: RegisterRequest): Promise<RegisterResponse> => {
        const response = await api.post<RegisterResponse>('/auth/register', data);
        return response.data;
    },

    changePassword: async (data: ChangePasswordRequest): Promise<{ success: boolean; message: string }> => {
        const response = await api.post<{ success: boolean; message: string }>('/auth/change-password', data);
        return response.data;
    },

    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearCachedAdmin();
    },

    getCurrentUser: () => {
        const userStr = localStorage.getItem('user');
        if (!userStr) return null;
        try {
            return normalizeStoredUser(JSON.parse(userStr));
        } catch {
            return null;
        }
    },

    getUserPermission: (): number => {
        return authService.getCurrentUser()?.permission ?? 0;
    },

    /** Verifica admin na API (Permission==100 no banco). Resultado fica em cache na sessão. */
    checkIsAdmin: async (force = false): Promise<boolean> => {
        if (!authService.isAuthenticated()) {
            clearCachedAdmin();
            return false;
        }

        if (!force) {
            const cached = readCachedAdmin();
            if (cached !== null) return cached;
            if (adminCheckInFlight) return adminCheckInFlight;
        }

        adminCheckInFlight = (async () => {
            try {
                const response = await api.get<{ isAdmin: boolean }>('/auth/admin-check');
                const isAdmin = !!response.data?.isAdmin;
                writeCachedAdmin(isAdmin);
                return isAdmin;
            } catch (err: unknown) {
                const cached = readCachedAdmin();
                if (cached !== null) return cached;
                const axiosErr = err as { response?: { status?: number } };
                if (axiosErr.response?.status === 429) {
                    return authService.getUserPermission() === 100;
                }
                return false;
            } finally {
                adminCheckInFlight = null;
            }
        })();

        return adminCheckInFlight;
    },

    isAuthenticated: (): boolean => {
        return !!localStorage.getItem('token');
    },
};

export const cashService = {
    getBalance: async (username: string): Promise<CashBalance> => {
        const response = await api.get<CashBalance>(`/cash/balance/${username}`);
        return response.data;
    },
};

export const cashShopService = {
    getCategories: async (): Promise<CashShopCategoryDto[]> => {
        const response = await api.get<CashShopCategoryDto[]>('/cashshop/categories');
        return Array.isArray(response.data) ? response.data : [];
    },
    getItems: async (category?: string): Promise<CashShopItemDto[]> => {
        const params = category ? { category } : {};
        const response = await api.get<CashShopItemDto[]>('/cashshop/items', { params });
        return Array.isArray(response.data) ? response.data : [];
    },
    getOutbox: async (characterDbKey: number): Promise<CashShopOutboxItemDto[]> => {
        const response = await api.get<CashShopOutboxItemDto[]>('/cashshop/outbox', { params: { characterDbKey } });
        return Array.isArray(response.data) ? response.data : [];
    },
    buy: async (recId: string, count: number, characterDbKey: number): Promise<{ success: boolean; message: string }> => {
        const response = await api.post<{ success: boolean; message: string }>('/cashshop/buy', {
            recId,
            count,
            characterDbKey,
        });
        return response.data;
    },
};

export const eventPackageService = {
    /** Valida um código promocional e devolve o pacote (público). */
    getInfo: async (code: string): Promise<EventPackageInfoDto> => {
        const response = await api.get<EventPackageInfoDto>('/eventpackage/info', { params: { code } });
        return response.data ?? { valid: false };
    },
    /** Status do resgate do usuário logado. */
    getStatus: async (): Promise<EventPackageStatusDto> => {
        const response = await api.get<EventPackageStatusDto>('/eventpackage/status');
        return response.data ?? { alreadyRedeemed: false };
    },
    /** Resgata o pacote do código para o personagem escolhido (1x por Username). */
    redeem: async (code: string, characterDbKey: number, className?: string): Promise<{
        success: boolean;
        message: string;
        payload?: EventPackageStatusDto;
    }> => {
        const response = await api.post<{ success: boolean; message: string; payload?: EventPackageStatusDto }>(
            '/eventpackage/redeem',
            { code, characterDbKey, className }
        );
        return response.data;
    },
    // Admin
    listPackages: async (): Promise<EventPackageAdminDto[]> => {
        const response = await api.get<EventPackageAdminDto[]>('/eventpackage/admin/packages');
        return Array.isArray(response.data) ? response.data : [];
    },
    createPackage: async (data: EventPackageAdminCreateRequest): Promise<{ success: boolean; id: number; code?: string }> => {
        const response = await api.post<{ success: boolean; id: number; code?: string }>('/eventpackage/admin/packages', data);
        return response.data;
    },
    updatePackage: async (id: number, data: EventPackageAdminCreateRequest): Promise<void> => {
        await api.put(`/eventpackage/admin/packages/${id}`, data);
    },
    deletePackage: async (id: number): Promise<void> => {
        await api.delete(`/eventpackage/admin/packages/${id}`);
    },
    regenerateCode: async (id: number, code?: string): Promise<{ success: boolean; code: string }> => {
        const response = await api.post<{ success: boolean; code: string }>(`/eventpackage/admin/packages/${id}/code`, { code });
        return response.data;
    },
    listRedemptions: async (): Promise<EventPackageRedemptionDto[]> => {
        const response = await api.get<EventPackageRedemptionDto[]>('/eventpackage/admin/redemptions');
        return Array.isArray(response.data) ? response.data : [];
    },
};

export const paymentService = {
    getPackages: async (): Promise<CashPackage[]> => {
        const response = await api.get<{ success: boolean; packages: CashPackage[] }>('/payments/packages');
        return response.data.packages;
    },

    createPreference: async (packageId: number): Promise<PaymentPreference> => {
        const response = await api.post<PaymentPreference>('/payments/create-preference', { packageId });
        return response.data;
    },

    reconcileReturn: async (params: {
        paymentId?: string;
        preferenceId?: string;
        externalReference?: string;
    }): Promise<{ success: boolean; message: string; status?: string }> => {
        const response = await api.post<{ success: boolean; message: string; status?: string }>(
            '/payments/reconcile-return',
            {
                paymentId: params.paymentId,
                preferenceId: params.preferenceId,
                externalReference: params.externalReference,
            }
        );
        return response.data;
    },
};

export const cryptoPaymentService = {
    createPayment: async (packageId: number): Promise<CryptoPaymentDto> => {
        const response = await api.post<CryptoPaymentResponse>('/payments/crypto/create', { packageId });
        return response.data.payment;
    },

    reconcileReturn: async (orderId: string): Promise<CryptoPaymentStatus> => {
        const response = await api.post<CryptoPaymentStatus>('/payments/crypto/reconcile-return', { orderId });
        return response.data;
    },

    getStatus: async (orderId: string): Promise<CryptoPaymentStatus> => {
        const response = await api.get<CryptoPaymentStatus>(`/payments/crypto/status/${encodeURIComponent(orderId)}`);
        return response.data;
    },
};

export const newsService = {
    getAllNews: async (): Promise<NewsItem[]> => {
        const response = await api.get<NewsItem[]>('/news');
        return response.data;
    },

    getNewsByCategory: async (category: string): Promise<NewsItem[]> => {
        const response = await api.get<NewsItem[]>(`/news/category/${category}`);
        return response.data;
    },

    getNewsById: async (id: number): Promise<NewsItem> => {
        const response = await api.get<NewsItem>(`/news/${id}`);
        return response.data;
    },

    createNews: async (data: Omit<NewsItem, 'id' | 'viewCount' | 'createdAt' | 'updatedAt'>): Promise<NewsItem> => {
        const response = await api.post<NewsItem>('/news', data);
        return response.data;
    },

    updateNews: async (id: number, data: Omit<NewsItem, 'createdAt' | 'updatedAt' | 'viewCount'>): Promise<void> => {
        await api.put(`/news/${id}`, { ...data, id });
    },

    deleteNews: async (id: number): Promise<void> => {
        await api.delete(`/news/${id}`);
    },
};

export const supportService = {
    getMyTickets: async () => {
        const response = await api.get('/support/my-tickets');
        return response.data;
    },

    createTicket: async (data: { subject: string; message: string; priority?: string }) => {
        const response = await api.post('/support/create-ticket', data);
        return response.data;
    },

    closeTicket: async (id: number) => {
        const response = await api.put(`/support/ticket/${id}/close`);
        return response.data;
    },
};

export const accountHistoryService = {
    getMyHistory: async () => {
        const response = await api.get('/accounthistory/my-history');
        return response.data;
    },

    logAction: async (action: string, description: string) => {
        const response = await api.post('/accounthistory/log-action', { action, description });
        return response.data;
    },
};

export const charactersService = {
    getMyCharacters: async () => {
        console.log('Fazendo chamada para /characters/my-characters');
        const response = await api.get('/characters/my-characters');
        console.log('Resposta da API:', response.data);
        return response.data;
    },

    getCharacterById: async (id: number) => {
        const response = await api.get(`/characters/character/${id}`);
        return response.data;
    },

    getCharacterStats: async (id: number) => {
        const response = await api.get(`/characters/stats/${id}`);
        return response.data;
    },

    getCharacterCount: async () => {
        const response = await api.get('/characters/count');
        return response.data;
    },
};

export const downloadLinksService = {
    getList: async (): Promise<DownloadLinkItem[]> => {
        const response = await api.get<DownloadLinkItem[]>('/downloadlinks');
        return Array.isArray(response.data) ? response.data : [];
    },
    getListAdmin: async (): Promise<DownloadLinkItem[]> => {
        const response = await api.get<DownloadLinkItem[]>('/downloadlinks/admin');
        return Array.isArray(response.data) ? response.data : [];
    },
    create: async (item: DownloadLinkItem): Promise<DownloadLinkItem> => {
        const response = await api.post<DownloadLinkItem>('/downloadlinks', item);
        return response.data;
    },
    update: async (id: number, item: DownloadLinkItem): Promise<void> => {
        await api.put(`/downloadlinks/${id}`, { ...item, id });
    },
    delete: async (id: number): Promise<void> => {
        await api.delete(`/downloadlinks/${id}`);
    },
};

export const siteConfigService = {
    getConfig: async (): Promise<SiteConfigData> => {
        const response = await api.get<string>('/siteconfig', { responseType: 'text' });
        try {
            return typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        } catch {
            return {};
        }
    },
    updateConfig: async (config: SiteConfigData): Promise<void> => {
        await api.put('/siteconfig/admin', config);
    },
};

export const countdownService = {
    getCountdown: async (): Promise<SiteCountdownData | null> => {
        const response = await api.get<SiteCountdownData>('/countdown');
        return response.data?.isEnabled ? response.data : null;
    },
    getCountdownAdmin: async (): Promise<SiteCountdownData> => {
        const response = await api.get<SiteCountdownData>('/countdown/admin');
        return response.data;
    },
    updateCountdown: async (data: SiteCountdownUpdateRequest): Promise<void> => {
        await api.put('/countdown/admin', data);
    },
};

export const uploadService = {
    uploadImage: async (file: File): Promise<{ success: boolean; url: string; fileName: string }> => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post<{ success: boolean; url: string; fileName: string }>('/upload/image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
    uploadVideo: async (file: File): Promise<{ success: boolean; url: string; fileName: string }> => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post<{ success: boolean; url: string; fileName: string }>('/upload/video', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const rankingsService = {
    getTower: async (top = 100, classFilter?: string): Promise<RankingRow[]> => {
        const params = new URLSearchParams();
        if (top) params.set('top', String(top));
        if (classFilter) params.set('classFilter', classFilter);
        const response = await api.get<RankingRow[]>(`/rankings/tower?${params.toString()}`);
        return Array.isArray(response.data) ? response.data : [];
    },
    getPk: async (top = 100, classFilter?: string): Promise<RankingRow[]> => {
        const params = new URLSearchParams();
        if (top) params.set('top', String(top));
        if (classFilter) params.set('classFilter', classFilter);
        const response = await api.get<RankingRow[]>(`/rankings/pk?${params.toString()}`);
        return Array.isArray(response.data) ? response.data : [];
    },
    getGuild: async (top = 100): Promise<GuildRankingRow[]> => {
        const params = new URLSearchParams();
        if (top) params.set('top', String(top));
        const response = await api.get<GuildRankingRow[]>(`/rankings/guild?${params.toString()}`);
        return Array.isArray(response.data) ? response.data : [];
    },
    getLevel: async (top = 100, classFilter?: string): Promise<LevelRankingRow[]> => {
        const params = new URLSearchParams();
        if (top) params.set('top', String(top));
        if (classFilter) params.set('classFilter', classFilter);
        const response = await api.get<LevelRankingRow[]>(`/rankings/level?${params.toString()}`);
        return Array.isArray(response.data) ? response.data : [];
    },
};

export const streamerService = {
    /** Lives ativas (público). */
    getStreamers: async (): Promise<StreamerPublicDto[]> => {
        const response = await api.get<StreamerPublicDto[]>('/streamers');
        return Array.isArray(response.data) ? response.data : [];
    },
    /** Todos os streamers cadastrados (admin). */
    getStreamersAdmin: async (): Promise<StreamerAdminDto[]> => {
        const response = await api.get<StreamerAdminDto[]>('/streamers/admin');
        return Array.isArray(response.data) ? response.data : [];
    },
    /** Busca contas pelo nome (admin). */
    searchAccounts: async (search: string): Promise<StreamerAccountSearchDto[]> => {
        const response = await api.get<StreamerAccountSearchDto[]>('/streamers/admin/accounts', { params: { search } });
        return Array.isArray(response.data) ? response.data : [];
    },
    /** Ativa/desativa streamer e define os links (admin). */
    updateStreamer: async (accountDbId: number, data: StreamerUpdateRequest): Promise<void> => {
        await api.put(`/streamers/admin/${accountDbId}`, data);
    },
};

export const guideService = {
    getTree: async (): Promise<GuideTreeCategory[]> => {
        const response = await api.get<GuideTreeCategory[]>('/guides/tree');
        return response.data;
    },
    getArticle: async (slug: string): Promise<GuideArticle> => {
        const response = await api.get<GuideArticle>(`/guides/article/${encodeURIComponent(slug)}`);
        return response.data;
    },
    getAdminCategories: async (): Promise<GuideCategory[]> => {
        const response = await api.get<GuideCategory[]>('/guides/admin/categories');
        return response.data;
    },
    createCategory: async (data: Omit<GuideCategory, 'id'>): Promise<GuideCategory> => {
        const response = await api.post<GuideCategory>('/guides/admin/categories', data);
        return response.data;
    },
    updateCategory: async (id: number, data: GuideCategory): Promise<void> => {
        await api.put(`/guides/admin/categories/${id}`, { ...data, id });
    },
    deleteCategory: async (id: number): Promise<void> => {
        await api.delete(`/guides/admin/categories/${id}`);
    },
    getAdminArticles: async (): Promise<GuideArticle[]> => {
        const response = await api.get<GuideArticle[]>('/guides/admin/articles');
        return response.data;
    },
    createArticle: async (data: Omit<GuideArticle, 'id'>): Promise<GuideArticle> => {
        const response = await api.post<GuideArticle>('/guides/admin/articles', data);
        return response.data;
    },
    updateArticle: async (id: number, data: GuideArticle): Promise<void> => {
        await api.put(`/guides/admin/articles/${id}`, { ...data, id });
    },
    deleteArticle: async (id: number): Promise<void> => {
        await api.delete(`/guides/admin/articles/${id}`);
    },
};

export const mountGuideService = {
    getHome: async (): Promise<MountGuideHome> => {
        const response = await api.get<MountGuideHome>('/mountguide/home');
        return response.data;
    },
    getRegion: async (slug: string): Promise<MountGuideRegionDetail> => {
        const response = await api.get<MountGuideRegionDetail>(`/mountguide/regions/${encodeURIComponent(slug)}`);
        return response.data;
    },
    getMount: async (slug: string): Promise<MountGuideMountDetail> => {
        const response = await api.get<MountGuideMountDetail>(`/mountguide/mounts/${encodeURIComponent(slug)}`);
        return response.data;
    },
    getAdminSections: async () => {
        const response = await api.get('/mountguide/admin/sections');
        return response.data;
    },
    createSection: async (data: Record<string, unknown>) => {
        const response = await api.post('/mountguide/admin/sections', data);
        return response.data;
    },
    updateSection: async (id: number, data: Record<string, unknown>) => {
        await api.put(`/mountguide/admin/sections/${id}`, data);
    },
    deleteSection: async (id: number) => {
        await api.delete(`/mountguide/admin/sections/${id}`);
    },
    getAdminRarities: async () => {
        const response = await api.get('/mountguide/admin/rarities');
        return response.data;
    },
    createRarity: async (data: Record<string, unknown>) => {
        const response = await api.post('/mountguide/admin/rarities', data);
        return response.data;
    },
    updateRarity: async (id: number, data: Record<string, unknown>) => {
        await api.put(`/mountguide/admin/rarities/${id}`, data);
    },
    deleteRarity: async (id: number) => {
        await api.delete(`/mountguide/admin/rarities/${id}`);
    },
    getAdminRegions: async () => {
        const response = await api.get('/mountguide/admin/regions');
        return response.data;
    },
    createRegion: async (data: Record<string, unknown>) => {
        const response = await api.post('/mountguide/admin/regions', data);
        return response.data;
    },
    updateRegion: async (id: number, data: Record<string, unknown>) => {
        await api.put(`/mountguide/admin/regions/${id}`, data);
    },
    deleteRegion: async (id: number) => {
        await api.delete(`/mountguide/admin/regions/${id}`);
    },
    getAdminMounts: async () => {
        const response = await api.get('/mountguide/admin/mounts');
        return response.data;
    },
    createMount: async (data: Record<string, unknown>) => {
        const response = await api.post('/mountguide/admin/mounts', data);
        return response.data;
    },
    updateMount: async (id: number, data: Record<string, unknown>) => {
        await api.put(`/mountguide/admin/mounts/${id}`, data);
    },
    deleteMount: async (id: number) => {
        await api.delete(`/mountguide/admin/mounts/${id}`);
    },
};

export default api;
